// src/components/dashboard/OutcomePieChart.tsx
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface OutcomeData {
  name: string;
  value: number;
}

interface OutcomePieChartProps {
  data: OutcomeData[] | undefined;
  isLoading: boolean;
}

// Define consistent colors - uses the original hardcoded list
// Note: With 11 outcomes and 7 colors, colors will repeat based on index.
const COLORS = ['#3A5AFF', '#38B382', '#FFB43A', '#525F7F', '#F2F4F7', '#FF6B6B', '#845EC2'];

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    // Hide label for slices smaller than 5%
    if (!percent || percent < 0.05) return null;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Basic white label - contrast might be poor on the light gray (#F2F4F7) slice
    return (
        <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize="10px" fontWeight="bold">
        {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};


export default function OutcomePieChart({ data, isLoading }: OutcomePieChartProps) {
  const hasData = data && data.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-medium text-muted-foreground">Call Outcomes</CardTitle>
      </CardHeader>
      <CardContent>
        <div style={{ width: '100%', height: 250 }}> {/* Fixed height container */}
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
               <Skeleton className="h-48 w-48 rounded-full" />
            </div>
          ) : hasData ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={80}
                  fill="#8884d8" // Default fill, overridden by Cell
                  dataKey="value"
                  nameKey="name" // Still important for Legend and Tooltip
                >
                  {/* *** FIX: Correctly use the numerical index from map *** */}
                  {data.map((_entry, index) => ( // Use _entry (or _) for unused element, get numerical 'index'
                    <Cell
                      key={`cell-${index}`}
                      // Use the correct 'index' with modulo for color assignment
                      fill={COLORS[index % COLORS.length]}
                      // Optional: Add a white stroke for separation if desired
                      // stroke="#FFFFFF"
                      />
                  ))}
                </Pie>
                {/* Keep Tooltip showing value and name */}
                <Tooltip formatter={(value: number, name: string) => [`${value} calls`, name]} />
                {/* Keep Legend showing names */}
                <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                    iconSize={10}
                    wrapperStyle={{ fontSize: '12px', lineHeight: '16px' }}
                    />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              No outcome data for this period.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}